<template>
  <!--
    FedEx end-customer registration with Multi-Factor Authentication (Additional Guidance, section 4).
    Factor 1: account number + billing address. Factor 2: secure code by email / SMS / call, or the
    details of a recent FedEx invoice. The fallback (FedEx Customer Service bypass) is only shown after
    Factor 2 fails, using FedEx's prescribed message.
  -->
  <v-card outlined rounded="lg" class="pa-4 fedex-registration">
    <div class="d-flex align-center mb-2">
      <FedExLogo :height="24" class="mr-3" />
      <span class="text-subtitle-1 font-weight-medium">Connect your FedEx account</span>
    </div>

    <v-alert v-if="stage === 'done'" type="success" dense border="left" class="mb-0">
      FedEx account {{ carrier.account_number }} is connected.
      <span v-if="bypassed">Identity confirmed through FedEx Customer Service.</span>
      Save the carrier to start shipping.
    </v-alert>

    <template v-else>
      <p class="text-body-2 grey--text text--darken-1 mb-3">
        FedEx needs to confirm that you own account {{ carrier.account_number || '…' }}. Step 1 checks the account number and
        the billing address registered with FedEx. Step 2 confirms your identity with a one-time secure code or a recent
        FedEx invoice. You only do this once per account.
      </p>

      <v-alert v-if="!eula_accepted" type="info" text dense border="left" class="mb-3">
        Read and accept the FedEx End User License Agreement before connecting the account.
        <div class="mt-2">
          <v-btn small rounded color="primary" type="button" @click="$emit('open-eula')">
            <v-icon left small>mdi-file-document-outline</v-icon>Read the FedEx EULA
          </v-btn>
        </div>
      </v-alert>

      <!-- Step 1 -->
      <template v-if="stage === 'start' || stage === 'support'">
        <v-alert v-if="stage === 'support'" type="warning" dense border="left" class="mb-3">{{ support_message }}</v-alert>
        <p v-if="stage === 'support'" class="text-body-2 grey--text text--darken-1 mb-3">
          Once FedEx Customer Service has verified your identity, validate the account and address again. FedEx will then
          connect the account without a second step.
        </p>
        <v-btn color="primary" rounded type="button" :disabled="!can_start" :loading="loading" @click="validate_address">
          <v-icon left>mdi-key</v-icon>{{ stage === 'support' ? 'Validate again' : 'Step 1: validate account and address' }}
        </v-btn>
        <div v-if="!can_start && eula_accepted" class="text-caption grey--text mt-2">
          Fill in the account number, company name and billing address above first.
        </div>
      </template>

      <!-- Step 2: choose method -->
      <template v-if="stage === 'method'">
        <v-alert type="success" text dense border="left" class="mb-3">Account and address validated.</v-alert>
        <p class="text-subtitle-2 font-weight-medium mb-1">Step 2: confirm your identity</p>
        <v-radio-group v-model="method" row class="mt-0" hide-details>
          <v-radio label="Secure code (PIN)" value="pin" />
          <v-radio label="Recent FedEx invoice" value="invoice" />
        </v-radio-group>

        <template v-if="method === 'pin'">
          <p class="text-body-2 mt-3 mb-2">Send the 6-digit secure code by</p>
          <v-btn-toggle v-model="pin_option" mandatory class="mb-3 flex-wrap">
            <v-btn v-for="option in pin_options" :key="option" :value="option" outlined rounded type="button" class="mr-2 mb-2">
              <v-icon left small>{{ option === 'EMAIL' ? 'mdi-email' : option === 'SMS' ? 'mdi-message-text' : 'mdi-phone' }}</v-icon>
              {{ option_label(option) }}
            </v-btn>
          </v-btn-toggle>
          <div>
            <v-btn color="primary" rounded type="button" :loading="loading" :disabled="!pin_option" @click="request_pin">Send code</v-btn>
          </div>
        </template>

        <template v-else>
          <p class="text-body-2 mt-3 mb-2">Enter the details of a FedEx invoice for this account that is no more than 90 days old.</p>
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-text-field v-model="invoice.number" label="Invoice number" outlined rounded dense hide-details="auto" />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field v-model="invoice.date" label="Invoice date" placeholder="YYYY-MM-DD" type="date" outlined rounded dense hide-details="auto" />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field v-model="invoice.amount" label="Invoice amount" placeholder="105.59" outlined rounded dense hide-details="auto" />
            </v-col>
            <v-col cols="12" sm="6">
              <v-combobox v-model="invoice.currency" :items="currencies" label="Currency" outlined rounded dense hide-details="auto" />
            </v-col>
          </v-row>
          <div class="mt-3">
            <v-btn color="primary" rounded type="button" :loading="loading" :disabled="!invoice_complete" @click="validate_invoice">Validate invoice</v-btn>
          </div>
        </template>
      </template>

      <!-- Step 2: enter the PIN -->
      <template v-if="stage === 'pin'">
        <v-alert type="success" text dense border="left" class="mb-3">
          Secure code sent by {{ option_label(pin_option) }}. It is valid for 10 minutes.
        </v-alert>
        <v-text-field
          v-model="pin"
          label="6-digit secure code"
          placeholder="123456"
          maxlength="7"
          inputmode="numeric"
          outlined
          rounded
          dense
          hide-details="auto"
          style="max-width: 260px"
          @keydown.enter.prevent="validate_pin"
        />
        <div class="d-flex flex-wrap align-center mt-3">
          <v-btn color="primary" rounded type="button" :loading="loading" :disabled="!/^\d{6,7}$/.test(pin)" @click="validate_pin">Validate code</v-btn>
          <v-btn text rounded type="button" :disabled="loading" @click="request_pin">Resend code</v-btn>
          <v-btn text rounded type="button" :disabled="loading" @click="stage = 'method'">Choose another method</v-btn>
        </div>
      </template>

      <v-alert v-for="(error, i) in errors" :key="`fedex-error-${i}`" type="error" dense border="left" class="mt-3 mb-0">
        <v-icon left small>mdi-alert-circle</v-icon>{{ error.message }}
      </v-alert>
    </template>

    <FedExDisclaimer class="mt-3" />
  </v-card>
</template>

<script>
import FedExDisclaimer from '@/components/fedex/FedExDisclaimer'
import FedExLogo from '@/components/fedex/FedExLogo'

export default {
  name: 'FedExRegistration',
  components: { FedExDisclaimer, FedExLogo },
  props: {
    carrier: { type: Object, required: true },
    company_id: { type: String, required: true },
    eula_accepted: { type: Boolean, default: false },
  },
  data(){
    return {
      stage: 'start', // start | method | pin | support | done
      loading: false,
      errors: [],
      support_message: '',
      bypassed: false,
      account_auth_token: '',
      mfa: {},
      method: 'pin',
      pin_options: ['EMAIL', 'SMS'],
      pin_option: 'EMAIL',
      pin: '',
      invoice: { number: '', date: '', amount: '', currency: 'EUR' },
      currencies: ['EUR', 'SEK', 'GBP', 'USD', 'DKK', 'NOK', 'CHF', 'PLN', 'CZK'],
    }
  },
  computed: {
    can_start(){
      const c = this.carrier || {}
      const address = c.address || {}
      const contact = c.contact || {}
      return !!(
        this.eula_accepted
        && /^\d{9}$/.test(String(c.account_number || '').trim())
        && (contact.companyName || contact.personName)
        && (address.streetLines || [])[0]
        && address.city && address.postalCode && address.countryCode
      )
    },
    invoice_complete(){
      const i = this.invoice
      return !!(i.number && i.date && i.amount && i.currency)
    },
  },
  methods: {
    option_label(option){
      if(option === 'EMAIL') return `Email${this.mfa.email ? ` (${this.mfa.email})` : ''}`
      if(option === 'SMS') return `SMS${this.mfa.phoneNumber ? ` (${this.mfa.phoneNumber})` : ''}`
      if(option === 'CALL') return `Phone call${this.mfa.phoneNumber ? ` (${this.mfa.phoneNumber})` : ''}`
      return option
    },
    async call(payload){
      this.loading = true
      this.errors = []
      try {
        const fedex_account_registration = this.$functions.httpsCallable('fedex_account_registration')
        const { data } = await fedex_account_registration({ ...payload, company_id: this.company_id })
        return data
      } catch (e) {
        let errors
        try { errors = JSON.parse(e.message) } catch (_) { errors = [{ code: 'ERROR', message: e.message || 'Unknown error' }] }
        if(!Array.isArray(errors)) errors = [errors]
        // Locked / too many attempts: only now are customers directed to FedEx Customer Service (fallback)
        const locked = errors.find(err => err.max_retry)
        if(locked){
          this.support_message = locked.message
          this.stage = 'support'
          this.errors = []
        } else {
          this.errors = errors
        }
        return null
      } finally {
        this.loading = false
      }
    },
    finish(auth, bypassed = false){
      this.bypassed = bypassed
      this.stage = 'done'
      this.$emit('authorized', auth)
    },
    async validate_address(){
      const c = this.carrier
      const data = await this.call({
        step: 'address',
        eula_accepted: this.eula_accepted,
        account_number: String(c.account_number).trim(),
        address: c.address,
        contact: c.contact,
      })
      if(!data) return
      if(data.auth) return this.finish(data.auth, !!data.bypassed)
      const { accountAuthToken, ...mfa } = (data.mfaOptions || [])[0] || {}
      if(!accountAuthToken){
        this.errors = [{ code: 'NO_MFA', message: 'FedEx did not offer any way to verify your identity. Please try again later.' }]
        return
      }
      this.account_auth_token = accountAuthToken
      this.mfa = mfa
      const offered = (mfa.options && mfa.options.secureCode) || []
      // PIN by phone call is only available in the US and Canada; FedEx tells us which options the account supports
      this.pin_options = offered.length ? offered : ['EMAIL', 'SMS']
      this.pin_option = this.pin_options[0]
      this.stage = 'method'
    },
    async request_pin(){
      const data = await this.call({ step: 'pin_request', option: this.pin_option, account_auth_token: this.account_auth_token })
      if(!data) return
      this.pin = ''
      this.stage = 'pin'
    },
    async validate_pin(){
      if(!/^\d{6,7}$/.test(this.pin)) return
      const data = await this.call({ step: 'pin_validate', secureCodePin: this.pin, account_auth_token: this.account_auth_token })
      if(data && data.auth) this.finish(data.auth)
    },
    async validate_invoice(){
      const data = await this.call({ step: 'invoice_validate', invoice: { ...this.invoice, amount: String(this.invoice.amount).replace(',', '.') }, account_auth_token: this.account_auth_token })
      if(data && data.auth) this.finish(data.auth)
    },
  },
}
</script>
